<script setup lang="ts">
import BetaAppearanceNavigation from '@/components/beta-dashboard/organisms/BetaAppearanceNavigation.vue'
import BetaPagesNavigation from '@/components/beta-dashboard/organisms/BetaPagesNavigation.vue'
import BetaSettingsNavigation from '@/components/beta-dashboard/organisms/BetaSettingsNavigation.vue'
import { useBetaPanelNavigation } from '@/components/beta-dashboard/useBetaPanelNavigation'
import modal from '@/services/modal'
import { useMediaManagerStore } from '@/stores/modal/media-manager'
import type { BetaPage, BetaSettingsSection } from '@/types/beta-dashboard'
import type { BetaAdvancedSettingsRouteName } from '@/utils/beta-settings-navigation'

const props = defineProps<{
    pages: BetaPage[]
    currentPageId: number | null
    loading?: boolean
}>()

const mediaManagerStore = useMediaManagerStore()
const { navItems, openingDesign, openDesign } = useBetaPanelNavigation({
    beforeOpenDesign: () => modal.confirmation(
        'Sair do editor?',
        'Esta ação poderá resultar na perda de dados não salvos.',
        {
            type: 'danger',
            textConfirm: 'Sim, sair',
            textCancel: 'Continuar editando',
        },
    ),
})

const emit = defineEmits<{
    home: []
    opportunities: []
    affiliates: []
    subscriptions: []
    help: []
    browsePages: []
    selectPage: [page: BetaPage]
    configurePage: [page: BetaPage]
    createPage: []
    openLayout: []
    openSettings: [section: BetaSettingsSection]
    openAdvancedSettings: [routeName: BetaAdvancedSettingsRouteName]
}>()

const navigationActions = {
    home: () => emit('home'),
    opportunities: () => emit('opportunities'),
    subscriptions: () => emit('subscriptions'),
    affiliates: () => emit('affiliates'),
    help: () => emit('help'),
}
</script>

<template>
    <aside v-show="!mediaManagerStore.isOpenModal" class="beta-editor-panel-navigation">
        <nav aria-label="Navegação do painel" class="beta-editor-panel-navigation__items">
            <template v-for="item in navItems" :key="item.id">
                <BetaPagesNavigation
                    v-if="item.id === 'content'"
                    :pages="props.pages"
                    :active="true"
                    :selected-page-id="props.currentPageId"
                    @browse="emit('browsePages')"
                    @select="emit('selectPage', $event)"
                    @configure="emit('configurePage', $event)"
                    @create="emit('createPage')"
                />
                <BetaAppearanceNavigation
                    v-else-if="item.id === 'appearance'"
                    :opening-design="openingDesign"
                    @design="openDesign"
                    @layout="emit('openLayout')"
                />
                <BetaSettingsNavigation
                    v-else-if="item.id === 'settings'"
                    :active="false"
                    section="identity"
                    @select="emit('openSettings', $event)"
                    @advanced="emit('openAdvancedSettings', $event)"
                />
                <button
                    v-else
                    type="button"
                    class="beta-editor-panel-navigation__item"
                    @click="navigationActions[item.id]()"
                >
                    <component :is="item.icon" class="beta-editor-panel-navigation__icon" />
                    <span>
                        <strong>
                            {{ item.label }}
                        </strong>
                    </span>
                </button>
            </template>
        </nav>

        <p v-if="props.loading && !props.pages.length" class="beta-editor-panel-navigation__loading">
            Carregando páginas…
        </p>
    </aside>
</template>

<style scoped>
.beta-editor-panel-navigation {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 60;
    display: flex;
    width: 255px;
    flex-direction: column;
    border-right: 1px solid #e3e9f0;
    background: #f3f7fb;
    padding: 16px;
}

.beta-editor-panel-navigation__items {
    display: grid;
    min-height: 0;
    align-content: start;
    gap: 4px;
    overflow-y: auto;
    padding-right: 2px;
}

.beta-editor-panel-navigation__item {
    display: grid;
    width: 100%;
    min-height: 54px;
    grid-template-columns: 34px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
    border-radius: 17px;
    padding: 9px 12px;
    color: #172132;
    text-align: left;
    transition: 160ms ease;
}

.beta-editor-panel-navigation__item:hover,
.beta-editor-panel-navigation__item:focus-visible {
    background: color-mix(in srgb, var(--beta-accent) 12%, white);
    color: var(--beta-accent);
}

.beta-editor-panel-navigation__item:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--beta-accent) 24%, transparent);
    outline-offset: 1px;
}

.beta-editor-panel-navigation__icon {
    box-sizing: content-box;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #edf1f6;
    padding: 9px;
    color: #7b8797;
}

.beta-editor-panel-navigation__item strong {
    display: block;
    font-size: 13px;
    font-weight: 500;
}

.beta-editor-panel-navigation__loading {
    margin-top: auto;
    padding: 10px 12px;
    color: #7b8594;
    font-size: 11px;
}

@media (max-width: 1023px) {
    .beta-editor-panel-navigation {
        display: none;
    }
}
</style>
